class LandingHero extends HTMLElement {
  constructor() {
    super();
    this.phonePlayer = this.querySelector('.landing-hero__phone-player');
    this.musicToggle = this.querySelector('.landing-hero__music-toggle');
    this.phonePlayerClose = this.querySelector('.landing-hero__phone-player-close');
    this.preloader = this.querySelector('.landing-hero__preloader');
    this.player = null;
    this.gearToggle = this.querySelector('.landing-hero__gear-toggle');
    this.gearPanel = this.nextElementSibling;
    this.miniPlayer = null;

    if (this.preloader) {
      this.initPreloader();
    }

    const playerEl = this.querySelector('.lh-player');
    if (playerEl) {
      this.player = new LHPlayer(playerEl);
      this.player.onUpdate = () => this.syncMiniPlayer();
      this.miniPlayer = this.createMiniPlayer();
    }

    if (this.gearToggle && this.gearPanel) {
      this.gearToggle.addEventListener('click', this.toggleGearShop.bind(this));
      this.gearPanel.classList.add('is-open');
      this.gearPanel.setAttribute('aria-hidden', 'false');
      this.gearToggle.setAttribute('aria-expanded', 'true');
    }

    this.infoToggle = this.querySelector('.landing-hero__info-toggle');
    if (this.infoToggle) {
      const infoPanelId = this.infoToggle.getAttribute('aria-controls');
      this.infoPanel = document.getElementById(infoPanelId);
      if (this.infoPanel) {
        this.infoToggle.addEventListener('click', this.toggleInfoPanel.bind(this));
        this.initInfoViewer();
      }
    }

    this.archiveToggle = this.querySelector('.landing-hero__archive-toggle');
    this.archivePanel = null;
    if (this.archiveToggle) {
      const archivePanelId = this.archiveToggle.getAttribute('aria-controls');
      this.archivePanel = document.getElementById(archivePanelId);
      if (this.archivePanel) {
        this.archiveToggle.addEventListener('click', this.toggleArchivePanel.bind(this));
        this.initDiskInteraction();
      }
    }

    if (this.musicToggle) {
      this.musicToggle.addEventListener('click', this.togglePlayer.bind(this));
    }

    if (this.phonePlayerClose) {
      this.phonePlayerClose.addEventListener('click', this.closePlayer.bind(this));
    }

  }

  createMiniPlayer() {
    const mp = document.createElement('div');
    mp.className = 'lh-mini-player';
    mp.setAttribute('aria-hidden', 'true');
    mp.innerHTML = `
      <div class="lh-mini-player__track">—</div>
      <div class="lh-mini-player__progress"><div class="lh-mini-player__progress-bar"></div></div>
      <div class="lh-mini-player__controls">
        <button type="button" class="lh-mini-player__btn lh-mini-player__btn--prev" aria-label="Previous">
          <svg viewBox="0 0 24 24" fill="currentColor" width="16" height="16"><path d="M6 6h2v12H6zm3.5 6 8.5 6V6z"/></svg>
        </button>
        <button type="button" class="lh-mini-player__btn lh-mini-player__btn--play" aria-label="Play">
          <svg class="icon-play" viewBox="0 0 24 24" fill="currentColor" width="18" height="18"><path d="M8 5v14l11-7z"/></svg>
          <svg class="icon-pause" viewBox="0 0 24 24" fill="currentColor" width="18" height="18"><path d="M6 19h4V5H6v14zm8-14v14h4V5h-4z"/></svg>
        </button>
        <button type="button" class="lh-mini-player__btn lh-mini-player__btn--next" aria-label="Next">
          <svg viewBox="0 0 24 24" fill="currentColor" width="16" height="16"><path d="m6 18 8.5-6L6 6v12zM16 6v12h2V6h-2z"/></svg>
        </button>
      </div>
    `;
    document.body.appendChild(mp);

    mp.querySelector('.lh-mini-player__btn--prev').addEventListener('click', () => this.player && this.player.prev());
    mp.querySelector('.lh-mini-player__btn--play').addEventListener('click', () => this.player && this.player.togglePlay());
    mp.querySelector('.lh-mini-player__btn--next').addEventListener('click', () => this.player && this.player.next());

    return mp;
  }

  syncMiniPlayer() {
    if (!this.miniPlayer || !this.player) return;
    const isPlaying = !this.player.audio.paused;
    const phoneOpen = this.phonePlayer && this.phonePlayer.classList.contains('is-open');
    const visible = isPlaying && !phoneOpen;

    this.miniPlayer.setAttribute('aria-hidden', visible ? 'false' : 'true');

    const trackEl = this.miniPlayer.querySelector('.lh-mini-player__track');
    if (trackEl && this.player.trackNameEl) {
      trackEl.textContent = this.player.trackNameEl.textContent;
    }

    const playBtn = this.miniPlayer.querySelector('.lh-mini-player__btn--play');
    if (playBtn) {
      playBtn.classList.toggle('is-playing', isPlaying);
      playBtn.setAttribute('aria-label', isPlaying ? 'Pause' : 'Play');
    }

    const bar = this.miniPlayer.querySelector('.lh-mini-player__progress-bar');
    const srcBar = this.player.progressBarEl;
    if (bar && srcBar) bar.style.width = srcBar.style.width;
  }

  initPreloader() {
    const minDuration = 1000;
    const start = Date.now();
    let pageLoaded = false;

    const hide = () => {
      if (!this.preloader) return;
      this.preloader.classList.add('is-hidden');
      setTimeout(() => {
        if (this.preloader) {
          this.preloader.remove();
          this.preloader = null;
        }
      }, 700);
    };

    const attemptHide = () => {
      if (!pageLoaded) return;
      const remaining = minDuration - (Date.now() - start);
      if (remaining <= 0) hide();
      else setTimeout(hide, remaining);
    };

    const onLoad = () => {
      pageLoaded = true;
      attemptHide();
    };

    if (document.readyState === 'complete') {
      onLoad();
    } else {
      window.addEventListener('load', onLoad, { once: true });
    }

    setTimeout(hide, minDuration + 10000);
  }

  closeInfoPanel() {
    if (!this.infoPanel) return;
    this.infoPanel.classList.remove('is-open');
    this.infoPanel.setAttribute('aria-hidden', 'true');
    if (this.infoToggle) this.infoToggle.setAttribute('aria-expanded', 'false');
    this.closeInfoViewer();
  }

  initInfoViewer() {
    this.infoPages = Array.from(this.infoPanel.querySelectorAll('.info-panel__page'));
    this.infoViewer = this.infoPanel.querySelector('.info-panel__viewer');
    if (!this.infoViewer || this.infoPages.length === 0) return;

    this.infoViewerTrack = this.infoViewer.querySelector('.info-panel__viewer-track');
    this.infoViewerSlides = Array.from(this.infoViewer.querySelectorAll('.info-panel__viewer-slide'));
    this.infoViewerDots = Array.from(this.infoViewer.querySelectorAll('.info-panel__viewer-dot'));
    this.infoViewerClose = this.infoViewer.querySelector('.info-panel__viewer-close');
    this.infoViewerPrev = this.infoViewer.querySelector('.info-panel__viewer-nav--prev');
    this.infoViewerNext = this.infoViewer.querySelector('.info-panel__viewer-nav--next');

    this.infoPages.forEach((page) => {
      page.addEventListener('click', () => this.openInfoViewer(0));
    });

    if (this.infoViewerClose) {
      this.infoViewerClose.addEventListener('click', () => this.closeInfoViewer());
    }
    if (this.infoViewerPrev) {
      this.infoViewerPrev.addEventListener('click', () => this.stepInfoViewer(-1));
    }
    if (this.infoViewerNext) {
      this.infoViewerNext.addEventListener('click', () => this.stepInfoViewer(1));
    }

    this.infoViewer.addEventListener('click', (event) => {
      if (event.target === this.infoViewer) this.closeInfoViewer();
    });

    this.infoViewerTrack.addEventListener('scroll', () => {
      clearTimeout(this._infoScrollTimer);
      this._infoScrollTimer = setTimeout(() => this.syncInfoDots(), 80);
    });

    this._infoKeyHandler = (event) => {
      if (!this.infoViewer.classList.contains('is-open')) return;
      if (event.key === 'Escape') this.closeInfoViewer();
      if (event.key === 'ArrowLeft') this.stepInfoViewer(-1);
      if (event.key === 'ArrowRight') this.stepInfoViewer(1);
    };
    document.addEventListener('keydown', this._infoKeyHandler);
  }

  openInfoViewer(index) {
    if (!this.infoViewer) return;
    this.infoViewer.classList.add('is-open');
    this.infoViewer.setAttribute('aria-hidden', 'false');
    document.body.style.overflow = 'hidden';
    this.goToInfoSlide(index, 'auto');
  }

  closeInfoViewer() {
    if (!this.infoViewer || !this.infoViewer.classList.contains('is-open')) return;
    this.infoViewer.classList.remove('is-open');
    this.infoViewer.setAttribute('aria-hidden', 'true');
    document.body.style.overflow = '';
  }

  goToInfoSlide(index, behavior) {
    const slide = this.infoViewerSlides[index];
    if (!slide || !this.infoViewerTrack) return;
    this.infoViewerTrack.scrollTo({ left: slide.offsetLeft, behavior: behavior || 'smooth' });
    this.syncInfoDots();
  }

  stepInfoViewer(direction) {
    const current = this.getCurrentInfoSlideIndex();
    const next = Math.min(Math.max(current + direction, 0), this.infoViewerSlides.length - 1);
    this.goToInfoSlide(next);
  }

  getCurrentInfoSlideIndex() {
    if (!this.infoViewerTrack) return 0;
    const scrollLeft = this.infoViewerTrack.scrollLeft;
    const width = this.infoViewerTrack.clientWidth || 1;
    return Math.round(scrollLeft / width);
  }

  syncInfoDots() {
    const current = this.getCurrentInfoSlideIndex();
    this.infoViewerDots.forEach((dot, index) => {
      dot.classList.toggle('is-active', index === current);
    });
  }

  closeGearShop() {
    if (!this.gearPanel) return;
    this.gearPanel.classList.remove('is-open');
    this.gearPanel.setAttribute('aria-hidden', 'true');
    if (this.gearToggle) this.gearToggle.setAttribute('aria-expanded', 'false');
  }

  toggleInfoPanel() {
    if (!this.infoPanel) return;
    const isOpen = this.infoPanel.classList.contains('is-open');
    if (isOpen) {
      this.closeInfoPanel();
    } else {
      this.closeGearShop();
      this.closeArchivePanel();
      this.infoPanel.classList.add('is-open');
      this.infoPanel.setAttribute('aria-hidden', 'false');
      this.infoToggle.setAttribute('aria-expanded', 'true');
      setTimeout(() => {
        this.infoPanel.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }, 50);
    }
  }

  toggleGearShop() {
    if (!this.gearPanel) return;
    const isOpen = this.gearPanel.classList.contains('is-open');
    if (isOpen) {
      this.closeGearShop();
    } else {
      this.closeInfoPanel();
      this.closeArchivePanel();
      this.gearPanel.classList.add('is-open');
      this.gearPanel.setAttribute('aria-hidden', 'false');
      this.gearToggle.setAttribute('aria-expanded', 'true');
      setTimeout(() => {
        this.gearPanel.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }, 50);
    }
  }

  closeArchivePanel() {
    if (!this.archivePanel) return;
    this.archivePanel.classList.remove('is-open');
    this.archivePanel.setAttribute('aria-hidden', 'true');
    if (this.archiveToggle) this.archiveToggle.setAttribute('aria-expanded', 'false');
    this.ejectDisk();
  }

  toggleArchivePanel() {
    if (!this.archivePanel) return;
    const isOpen = this.archivePanel.classList.contains('is-open');
    if (isOpen) {
      this.closeArchivePanel();
    } else {
      this.closeGearShop();
      this.closeInfoPanel();
      this.archivePanel.classList.add('is-open');
      this.archivePanel.setAttribute('aria-hidden', 'false');
      this.archiveToggle.setAttribute('aria-expanded', 'true');
      setTimeout(() => {
        this.archivePanel.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }, 50);
    }
  }

  ejectDisk() {
    if (!this.archivePanel) return;
    this.archivePanel.querySelectorAll('.archive-terminal-panel__disk').forEach(d => {
      d.classList.remove('is-inserted');
    });
    const active = this.archivePanel.querySelector('.archive-terminal-panel__screen-active');
    if (active) { active.classList.remove('is-visible'); active.setAttribute('aria-hidden', 'true'); }
    const idle = this.archivePanel.querySelector('.archive-terminal-panel__screen-idle');
    if (idle) idle.classList.remove('is-hidden');
  }

  initDiskInteraction() {
    const disks = this.archivePanel.querySelectorAll('.archive-terminal-panel__disk');
    const screenActive = this.archivePanel.querySelector('.archive-terminal-panel__screen-active');
    const screenIdle = this.archivePanel.querySelector('.archive-terminal-panel__screen-idle');
    const playMeImg = this.archivePanel.querySelector('.archive-terminal-panel__play-me-img');
    const playMeBtn = this.archivePanel.querySelector('.archive-terminal-panel__play-me-btn');

    disks.forEach(disk => {
      disk.addEventListener('click', () => {
        const alreadyInserted = disk.classList.contains('is-inserted');
        disks.forEach(d => d.classList.remove('is-inserted'));

        if (alreadyInserted) {
          if (screenActive) { screenActive.classList.remove('is-visible'); screenActive.setAttribute('aria-hidden', 'true'); }
          if (screenIdle) screenIdle.classList.remove('is-hidden');
        } else {
          disk.classList.add('is-inserted');
          if (playMeImg) {
            const src = disk.dataset.playMeSrc;
            playMeImg.src = src || '';
            playMeImg.style.display = src ? '' : 'none';
          }
          if (playMeBtn) playMeBtn.href = disk.dataset.link || '#';
          if (screenIdle) screenIdle.classList.add('is-hidden');
          if (screenActive) {
            screenActive.setAttribute('aria-hidden', 'false');
            requestAnimationFrame(() => { screenActive.classList.add('is-visible'); });
          }
        }
      });
    });
  }

  togglePlayer() {
    if (!this.phonePlayer) return;
    if (this.phonePlayer.classList.contains('is-open')) {
      this.closePlayer();
    } else {
      this.phonePlayer.classList.add('is-open');
      if (this.musicToggle) this.musicToggle.setAttribute('aria-expanded', 'true');
      if (this.miniPlayer) this.miniPlayer.setAttribute('aria-hidden', 'true');
      if (this.player) this.player.play();
    }
  }

  closePlayer() {
    if (!this.phonePlayer) return;
    this.phonePlayer.classList.remove('is-open');
    if (this.musicToggle) this.musicToggle.setAttribute('aria-expanded', 'false');
    // Music keeps playing — just sync the mini-player visibility
    this.syncMiniPlayer();
  }

}

class LHPlayer {
  constructor(el) {
    this.el = el;
    this.tracks = JSON.parse(el.dataset.tracks || '[]');
    this.current = 0;
    this.audio = new Audio();
    this.onUpdate = null;

    this.trackNameEl = el.querySelector('.lh-player__track-name');
    this.progressBarEl = el.querySelector('.lh-player__progress-bar');
    this.playBtn = el.querySelector('.lh-player__btn--play');
    this.prevBtn = el.querySelector('.lh-player__btn--prev');
    this.nextBtn = el.querySelector('.lh-player__btn--next');

    this.audio.addEventListener('timeupdate', () => {
      this.updateProgress();
      if (this.onUpdate) this.onUpdate();
    });
    this.audio.addEventListener('ended', () => this.next());
    this.audio.addEventListener('play', () => {
      this.setPlayState(true);
      if (this.onUpdate) this.onUpdate();
    });
    this.audio.addEventListener('pause', () => {
      this.setPlayState(false);
      if (this.onUpdate) this.onUpdate();
    });

    if (this.playBtn) this.playBtn.addEventListener('click', () => this.togglePlay());
    if (this.prevBtn) this.prevBtn.addEventListener('click', () => this.prev());
    if (this.nextBtn) this.nextBtn.addEventListener('click', () => this.next());

    if (this.tracks.length > 0) this.loadTrack(0, false);
  }

  loadTrack(index, autoPlay) {
    const track = this.tracks[index];
    if (!track) return;
    this.current = index;
    this.audio.src = track.url;
    if (this.trackNameEl) this.trackNameEl.textContent = track.label || '—';
    if (this.progressBarEl) this.progressBarEl.style.width = '0%';
    if (autoPlay) this.audio.play().catch(() => {});
    if (this.onUpdate) this.onUpdate();
  }

  play() {
    if (this.tracks.length === 0) return;
    this.audio.play().catch(() => {});
  }

  pause() {
    this.audio.pause();
  }

  togglePlay() {
    if (this.audio.paused) this.play();
    else this.pause();
  }

  next() {
    if (this.tracks.length === 0) return;
    const wasPlaying = !this.audio.paused;
    this.loadTrack((this.current + 1) % this.tracks.length, wasPlaying);
  }

  prev() {
    if (this.tracks.length === 0) return;
    const wasPlaying = !this.audio.paused;
    if (this.audio.currentTime > 3) {
      this.audio.currentTime = 0;
    } else {
      this.loadTrack((this.current - 1 + this.tracks.length) % this.tracks.length, wasPlaying);
    }
  }

  toggleMute() {
    this.audio.muted = !this.audio.muted;
  }

  setPlayState(playing) {
    if (!this.playBtn) return;
    this.playBtn.classList.toggle('is-playing', playing);
    this.playBtn.setAttribute('aria-label', playing ? 'Pause' : 'Play');
  }

  updateProgress() {
    if (!this.progressBarEl || !this.audio.duration) return;
    this.progressBarEl.style.width = (this.audio.currentTime / this.audio.duration * 100) + '%';
  }
}

customElements.define('landing-hero', LandingHero);
